import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, RefreshCw, Check, Package, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';

const PickSlipGeneration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  
  const [pickSlips, setPickSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchPickSlips = async () => {
    try {
      setLoading(true);
      const data = await greigeStorageApi.getPickSlips();
      setPickSlips(data.pick_slips || []);
    } catch (error) {
      toast.error('Failed to fetch pick slips');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPickSlips();
  }, []);

  useEffect(() => {
    if (requestId) {
      handleGenerate(requestId);
    }
  }, [requestId]);

  const handleGenerate = async (reqId) => {
    try {
      setGenerating(true);
      const result = await greigeStorageApi.generatePickSlip(reqId);
      toast.success('Pick slip generated');
      fetchPickSlips();
      // Clear the URL param
      navigate('/sections/greige-storage/pick-slip', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate pick slip');
    } finally {
      setGenerating(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      generated: { color: 'bg-blue-100 text-blue-700', label: 'Generated' },
      assigned: { color: 'bg-yellow-100 text-yellow-700', label: 'Assigned' },
      in_progress: { color: 'bg-orange-100 text-orange-700', label: 'In Progress' },
      completed: { color: 'bg-green-100 text-green-700', label: 'Completed' }
    };
    const c = config[status] || config.generated;
    return <Badge className={`${c.color} border-0`}>{c.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/greige-storage')} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Pick Slip Generation</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Storage</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchPickSlips}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {generating && (
          <Card className="border-blue-200 bg-blue-50 mb-6">
            <CardContent className="py-4 flex items-center gap-3">
              <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-blue-700">Generating pick slip...</span>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
          </div>
        ) : pickSlips.length === 0 ? (
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Pick Slips</h3>
              <p className="text-[#5A5F6E]">Generate pick slips from accepted shipment requests</p>
              <Button 
                onClick={() => navigate('/sections/greige-storage/shipment-request')}
                className="mt-4 bg-[#2D5F4F] hover:bg-[#1A1F2E]"
              >
                View Shipment Requests
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {pickSlips.map((slip) => (
              <Card key={slip.pick_slip_id} className="border-[#E5DFD0]" data-testid={`pick-slip-${slip.pick_slip_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-display">{slip.pick_slip_id}</CardTitle>
                    {getStatusBadge(slip.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 text-sm">
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Source Design</p>
                      <p className="font-medium">{slip.source_design_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Target Design</p>
                      <p className="font-medium">{slip.target_design_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Color</p>
                      <p className="font-medium">{slip.color}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Requested</p>
                      <p className="font-medium">{slip.requested_qty}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Allocated</p>
                      <p className="font-medium">{slip.allocated_qty}</p>
                    </div>
                  </div>

                  {/* Bundles Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-xs">#</TableHead>
                          <TableHead className="text-xs">Bundle</TableHead>
                          <TableHead className="text-xs">Location</TableHead>
                          <TableHead className="text-xs">Sort No</TableHead>
                          <TableHead className="text-xs">Weight</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {slip.bundles?.slice(0, 10).map((bundle, idx) => (
                          <TableRow key={bundle.bundle_barcode}>
                            <TableCell className="text-xs">{idx + 1}</TableCell>
                            <TableCell className="font-mono text-xs">{bundle.bundle_barcode}</TableCell>
                            <TableCell className="text-xs">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-gray-400" />
                                {bundle.footprint}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs">{bundle.sort_no}</TableCell>
                            <TableCell className="text-xs">{bundle.weight_kg?.toFixed(2)} kg</TableCell>
                            <TableCell>
                              {bundle.picked ? (
                                <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                                  <Check className="w-3 h-3 mr-1" />
                                  Picked
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">Pending</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {slip.bundles?.length > 10 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-xs text-gray-500">
                              +{slip.bundles.length - 10} more bundles
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {slip.status === 'generated' && (
                    <Button 
                      onClick={() => navigate(`/sections/greige-storage/picking?slip=${slip.pick_slip_id}`)}
                      className="mt-4 bg-[#2D5F4F] hover:bg-[#1A1F2E]"
                    >
                      Assign Picker
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PickSlipGeneration;
